import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalDoc = 'privacy' | 'terms' | null;
const legalContent: Record<Exclude<LegalDoc, null>, {
  title: string;
  sections: {
    heading: string;
    body: string;
  }[];
}> = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Introducción',
      body: 'En Luca Reyes respetamos tu privacidad y nos comprometemos a proteger la información personal que compartes con nosotros. Esta Política de Privacidad describe cómo recopilamos, usamos y protegemos tu información cuando visitas nuestro sitio web o interactúas con nuestro contenido.'
    }, {
      heading: '2. Información que Recopilamos',
      body: 'Podemos recopilar información que nos proporcionas voluntariamente al suscribirte a nuestros boletines, comentar en publicaciones o participar en nuestras comunidades. También recopilamos automáticamente datos de navegación como dirección IP, tipo de navegador y páginas visitadas mediante cookies y tecnologías similares.'
    }, {
      heading: '3. Uso de la Información',
      body: 'Utilizamos la información recopilada para mejorar tu experiencia en el sitio, enviarte contenido relevante sobre marketing de afiliados y emprendimiento digital, analizar tendencias de uso y cumplir con obligaciones legales aplicables.'
    }, {
      heading: '4. Compartir Información',
      body: 'No vendemos, alquilamos ni compartimos tu información personal con terceros para fines comerciales. Podemos compartir datos con proveedores de servicios que nos ayudan a operar el sitio, siempre bajo estrictos acuerdos de confidencialidad.'
    }, {
      heading: '5. Cookies',
      body: 'Utilizamos cookies para mejorar la funcionalidad del sitio y personalizar tu experiencia. Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar algunas funcionalidades del sitio.'
    }, {
      heading: '6. Seguridad',
      body: 'Implementamos medidas técnicas y organizativas razonables para proteger tu información personal contra acceso no autorizado, pérdida o alteración. Sin embargo, ningún sistema es completamente seguro y no podemos garantizar la seguridad absoluta.'
    }, {
      heading: '7. Tus Derechos',
      body: 'Tienes derecho a acceder, corregir o eliminar tu información personal, así como a oponerte al procesamiento de tus datos. Para ejercer estos derechos, puedes hacerlo a través de las opciones disponibles en nuestras comunicaciones.'
    }, {
      heading: '8. Cambios a esta Política',
      body: 'Podemos actualizar esta Política de Privacidad periódicamente. Te recomendamos revisarla con regularidad. Los cambios entran en vigor cuando se publican en esta página.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Aceptación de los Términos',
      body: 'Al acceder y utilizar este sitio web, aceptas cumplir con los presentes Términos y Condiciones. Si no estás de acuerdo con alguna parte, te pedimos que no utilices el sitio.'
    }, {
      heading: '2. Uso del Contenido',
      body: 'Todo el contenido publicado en este sitio, incluyendo textos, gráficos, imágenes, videos y materiales educativos, es propiedad de Luca Reyes o se utiliza con autorización. Está prohibida la reproducción, distribución o modificación sin consentimiento previo por escrito.'
    }, {
      heading: '3. Naturaleza Educativa',
      body: 'El contenido de este sitio tiene fines exclusivamente educativos e informativos. La información compartida sobre marketing de afiliados, emprendimiento digital y estrategias de monetización no constituye asesoramiento financiero, legal o profesional.'
    }, {
      heading: '4. Sin Garantía de Resultados',
      body: 'Los resultados obtenidos mediante la aplicación de las estrategias compartidas varían según el esfuerzo, la disciplina y las circunstancias individuales. No garantizamos ingresos específicos ni resultados particulares. El éxito depende de tu compromiso, ejecución y constancia.'
    }, {
      heading: '5. Enlaces de Afiliados',
      body: 'Este sitio puede contener enlaces de afiliados. Si realizas una compra a través de ellos, podemos recibir una comisión sin costo adicional para ti. Solo recomendamos productos y servicios que consideramos valiosos.'
    }, {
      heading: '6. Limitación de Responsabilidad',
      body: 'En ningún caso Luca Reyes será responsable por daños directos, indirectos, incidentales o consecuentes derivados del uso o la imposibilidad de uso del sitio o su contenido.'
    }, {
      heading: '7. Conducta del Usuario',
      body: 'Te comprometes a utilizar el sitio de manera legal y respetuosa. Está prohibido publicar contenido ofensivo, intentar acceder sin autorización a sistemas, o usar el sitio para actividades fraudulentas.'
    }, {
      heading: '8. Modificaciones',
      body: 'Nos reservamos el derecho de modificar estos Términos en cualquier momento. Los cambios entran en vigor al publicarse. El uso continuado del sitio implica la aceptación de los términos actualizados.'
    }, {
      heading: '9. Legislación Aplicable',
      body: 'Estos Términos se rigen por las leyes aplicables en la jurisdicción correspondiente. Cualquier disputa será resuelta de acuerdo con dichas leyes.'
    }]
  }
};
export function Footer() {
  const [openDoc, setOpenDoc] = useState<LegalDoc>(null);
  useEffect(() => {
    if (openDoc) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openDoc]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenDoc(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  const activeContent = openDoc ? legalContent[openDoc] : null;
  return <>
      <footer className="bg-[#0a0a0b] border-t border-white/5 pt-16 pb-8" data-id="element-22">
        <div className="container mx-auto px-6" data-id="element-23">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-12" data-id="element-24">
            <div className="flex flex-col items-center md:items-start gap-2" data-id="element-25">
              <div className="flex items-center gap-2" data-id="element-26">
                <div className="w-2 h-2 rounded-full bg-amber-400" data-id="element-27" />
                <span className="text-lg font-bold tracking-widest text-zinc-100 uppercase" data-id="element-28">
                  Luca Reyes
                </span>
              </div>
              <p className="text-sm text-zinc-500" data-id="element-29">
                Construyendo en la nueva economía digital.
              </p>
            </div>
          </div>

          <div className="text-center md:text-left text-xs text-zinc-600 flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-white/5" data-id="element-30">
            <p data-id="element-31">
              &copy; {new Date().getFullYear()} Luca Reyes. Todos los derechos
              reservados.
            </p>
            <div className="flex gap-6" data-id="element-32">
              <button onClick={() => setOpenDoc('privacy')} className="hover:text-amber-400 transition-colors" data-id="element-33">
                Privacidad
              </button>
              <button onClick={() => setOpenDoc('terms')} className="hover:text-amber-400 transition-colors" data-id="element-34">
                Términos
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence data-id="element-35">
        {openDoc && activeContent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-36">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setOpenDoc(null)} data-id="element-37" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-full max-w-2xl max-h-[85vh] bg-[#0f0f10] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-38">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-white/5 flex-shrink-0" data-id="element-39">
                <div className="flex items-center gap-3" data-id="element-40">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-41" />
                  <h2 id="legal-modal-title" className="text-xl font-bold text-zinc-100" data-id="element-42">
                    {activeContent.title}
                  </h2>
                </div>
                <button onClick={() => setOpenDoc(null)} className="w-8 h-8 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-100 hover:bg-white/5 transition-colors" aria-label="Cerrar" data-id="element-43">
                  <X className="w-4 h-4" data-id="element-44" />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto px-8 py-6 space-y-6 flex-1" data-id="element-45">
                <p className="text-xs text-zinc-500 uppercase tracking-wider" data-id="element-46">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long'
              })}
                </p>
                {activeContent.sections.map((section, i) => <div key={i} data-id="element-47">
                    <h3 className="text-sm font-bold text-amber-400 mb-2 tracking-wide" data-id="element-48">
                      {section.heading}
                    </h3>
                    <p className="text-sm text-zinc-400 leading-relaxed" data-id="element-49">
                      {section.body}
                    </p>
                  </div>)}
              </div>

              {/* Footer */}
              <div className="px-8 py-4 border-t border-white/5 flex justify-end flex-shrink-0 bg-[#0a0a0b]" data-id="element-50">
                <button onClick={() => setOpenDoc(null)} className="px-5 py-2 text-sm font-medium text-[#0a0a0b] bg-amber-400 rounded-full hover:bg-amber-300 transition-colors" data-id="element-51">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}